// Utility functions for the flexible role system

/**
 * Role capability mappings based on the platform architecture
 */
export const ROLE_CAPABILITIES = {
  general: {
    canApplyForJobs: false, // Can be enabled after completing job seeker wizard
    canBookServices: true,
    canProvideServices: false, // Can be enabled after completing employer wizard
    canPostJobs: false, // Can be enabled after completing employer wizard
    isAdmin: false
  },
  jobseeker: {
    canApplyForJobs: true,
    canBookServices: true,
    canProvideServices: false,
    canPostJobs: false,
    isAdmin: false
  },
  serviceprovider: {
    canApplyForJobs: true,
    canBookServices: true,
    canProvideServices: true,
    canPostJobs: true, // optional
    isAdmin: false
  },
  employer: {
    canApplyForJobs: true, // optional
    canBookServices: true, // optional
    canProvideServices: true, // optional
    canPostJobs: true,
    isAdmin: false
  },
  admin: {
    canApplyForJobs: true,
    canBookServices: true,
    canProvideServices: true,
    canPostJobs: true,
    isAdmin: true
  }
};

/**
 * Get active roles based on user capabilities
 * @param {Object} capabilities - User capabilities object
 * @returns {Array} Array of active role strings
 */
export const getActiveRoles = (capabilities) => {
  const roles = [];
  
  if (capabilities.canApplyForJobs && !capabilities.canPostJobs && !capabilities.canProvideServices) {
    roles.push('jobseeker');
  }
  
  if (capabilities.canProvideServices) {
    roles.push('serviceprovider');
  }
  
  if (capabilities.canPostJobs) {
    roles.push('employer');
  }
  
  if (capabilities.isAdmin) {
    roles.push('admin');
  }
  
  return roles;
};

/**
 * Check if user has a specific capability
 * @param {Object} user - User object with capabilities
 * @param {string} capability - Capability to check
 * @returns {boolean} Whether user has the capability
 */
export const hasCapability = (user, capability) => {
  // Super admin has unrestricted access (God Mode)
  if (user?.role === 'super_admin') return true;
  return user?.capabilities?.[capability] || false;
};

/**
 * Check if user has any of the specified capabilities
 * @param {Object} user - User object with capabilities
 * @param {Array} capabilities - Array of capabilities to check
 * @returns {boolean} Whether user has any of the capabilities
 */
export const hasAnyCapability = (user, capabilities) => {
  // Super admin has unrestricted access (God Mode)
  if (user?.role === 'super_admin') return true;
  return capabilities.some(capability => hasCapability(user, capability));
};

/**
 * Check if user has all of the specified capabilities
 * @param {Object} user - User object with capabilities
 * @param {Array} capabilities - Array of capabilities to check
 * @returns {boolean} Whether user has all of the capabilities
 */
export const hasAllCapabilities = (user, capabilities) => {
  // Super admin has unrestricted access (God Mode)
  if (user?.role === 'super_admin') return true;
  return capabilities.every(capability => hasCapability(user, capability));
};

/**
 * Get dashboard path based on user's primary role
 * @param {string} primaryRole - User's primary role
 * @returns {string} Dashboard path
 */
export const getDashboardPath = (primaryRole) => {
  switch (primaryRole) {
    case 'admin':
      return '/admin';
    case 'employer':
      return '/employer';
    case 'serviceprovider':
      return '/service-provider';
    case 'jobseeker':
      return '/jobseeker';
    case 'general':
      return '/dashboard'; // Unified dashboard for general users
    default:
      return '/dashboard';
  }
};

/**
 * Get profile path based on user's primary role
 * @param {string} primaryRole - User's primary role
 * @returns {string} Profile path
 */
export const getProfilePath = (primaryRole) => {
  switch (primaryRole) {
    case 'admin':
      return '/admin';
    case 'employer':
      return '/employer/profile';
    case 'serviceprovider':
      return '/service-provider/profile';
    case 'jobseeker':
      return '/jobseeker/profile';
    case 'general':
      return '/profile'; // General profile for general users
    default:
      return '/profile';
  }
};

/**
 * Toggle a capability for a user
 * @param {Object} currentCapabilities - Current user capabilities
 * @param {string} capability - Capability to toggle
 * @returns {Object} Updated capabilities object
 */
export const toggleCapability = (currentCapabilities, capability) => {
  return {
    ...currentCapabilities,
    [capability]: !currentCapabilities[capability]
  };
};

/**
 * Enable a role for a user (sets all required capabilities)
 * @param {Object} currentCapabilities - Current user capabilities
 * @param {string} role - Role to enable
 * @returns {Object} Updated capabilities object
 */
export const enableRole = (currentCapabilities, role) => {
  const roleCapabilities = ROLE_CAPABILITIES[role];
  if (!roleCapabilities) return currentCapabilities;
  
  return {
    ...currentCapabilities,
    ...roleCapabilities
  };
};

/**
 * Disable a role for a user (removes role-specific capabilities while preserving others)
 * @param {Object} currentCapabilities - Current user capabilities
 * @param {string} role - Role to disable
 * @returns {Object} Updated capabilities object
 */
export const disableRole = (currentCapabilities, role) => {
  const updatedCapabilities = { ...currentCapabilities };
  
  switch (role) {
    case 'jobseeker':
      // Only disable if user doesn't have other roles that need these capabilities
      if (!updatedCapabilities.canPostJobs && !updatedCapabilities.canProvideServices) {
        updatedCapabilities.canApplyForJobs = false;
        updatedCapabilities.canBookServices = false;
      }
      break;
    case 'serviceprovider':
      updatedCapabilities.canProvideServices = false;
      break;
    case 'employer':
      updatedCapabilities.canPostJobs = false;
      break;
    case 'admin':
      updatedCapabilities.isAdmin = false;
      break;
    default:
      // No changes for unknown roles
      break;
  }
  
  return updatedCapabilities;
};

/**
 * Get available actions for a user based on their capabilities
 * @param {Object} capabilities - User capabilities
 * @returns {Object} Available actions object
 */
export const getAvailableActions = (capabilities) => {
  return {
    canApplyToJobs: capabilities.canApplyForJobs,
    canBookServices: capabilities.canBookServices,
    canCreateServices: capabilities.canProvideServices,
    canManageServices: capabilities.canProvideServices,
    canPostJobs: capabilities.canPostJobs,
    canManageJobs: capabilities.canPostJobs,
    canViewApplicants: capabilities.canPostJobs,
    canAccessAdmin: capabilities.isAdmin,
    canManageUsers: capabilities.isAdmin,
    canApproveContent: capabilities.isAdmin
  };
};

/**
 * Validate if a user can perform a specific action
 * @param {Object} user - User object
 * @param {string} action - Action to validate
 * @returns {boolean} Whether user can perform the action
 */
export const canPerformAction = (user, action) => {
  // Super admin has unrestricted access (God Mode)
  if (user?.role === 'super_admin') return true;
  const actions = getAvailableActions(user.capabilities || {});
  return actions[action] || false;
};